import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Button, TextField, Alert
} from '@mui/material';

/**
 * Deleting an account (21 Oct 2026, ST-20261021-0001). Replaces the browser confirm:
 * the same warning, plus an optional box to say why. The reason is kept with the
 * deletion record and passed to the person's therapist.
 */
const MAX_REASON = 1000;

const DeleteAccountDialog = ({ open, onClose, onConfirm }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const close = () => {
    if (busy) return;
    setReason('');
    setError('');
    onClose();
  };

  const confirm = async () => {
    setBusy(true);
    setError('');
    try {
      await onConfirm(reason.trim());
    } catch (err) {
      setError(err?.message || 'Your account could not be deleted. Please try again.');
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>Delete your account?</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          This cannot be undone. Your account and all your conversations will be deleted.
        </DialogContentText>
        <DialogContentText component="label" htmlFor="delete-account-reason" sx={{ display: 'block', mb: 1, color: 'text.primary' }}>
          Would you like to tell us why you are leaving? (optional)
        </DialogContentText>
        <TextField
          id="delete-account-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, MAX_REASON))}
          multiline
          minRows={3}
          fullWidth
          disabled={busy}
          helperText="If you have a therapist, they will see what you write here."
        />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>Keep my account</Button>
        <Button onClick={confirm} color="error" variant="contained" disabled={busy}>
          {busy ? 'Deleting…' : 'Delete my account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteAccountDialog;
